import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Trophy, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Helper: default endDate = 7 days from now, formatted for <input type="datetime-local" />.
 */
function defaultEnd() {
  const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  // toISOString in local timezone for datetime-local input
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function NewRound() {
  const [products, setProducts] = useState([]);
  const [existingCampaignProductIds, setExistingCampaignProductIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [winnersCount, setWinnersCount] = useState(2);
  const [endDate, setEndDate] = useState(defaultEnd());
  const [preOrderPrice, setPreOrderPrice] = useState("");
  const [estimatedShipDate, setEstimatedShipDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.listProducts(), api.listCampaigns()]).then(([prods, camps]) => {
      setProducts(prods);
      // Block any product already in a campaign (standalone or round).
      // listCampaigns returns all campaigns including those inside rounds.
      setExistingCampaignProductIds(new Set(camps.map((c) => c.productId)));
    });
  }, []);

  const toggle = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (selectedIds.length < 2) {
      toast.error("Pick at least 2 products to compete");
      return;
    }
    if (winnersCount >= selectedIds.length) {
      toast.error("Winners must be fewer than the number of competing products");
      return;
    }
    if (new Date(endDate).getTime() <= Date.now()) {
      toast.error("End date must be in the future");
      return;
    }
    setSubmitting(true);
    try {
      // Convert local datetime to ISO with timezone (Z = UTC)
      const isoEnd = new Date(endDate).toISOString();
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        winnersCount: Number(winnersCount),
        endDate: isoEnd,
        productIds: selectedIds,
        preOrderPrice: preOrderPrice ? Number(preOrderPrice) : null,
        estimatedShipDate: estimatedShipDate || null,
      };
      const r = await api.createRound(payload);
      toast.success("Round created");
      navigate(`/app/rounds/${r.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create round");
    } finally {
      setSubmitting(false);
    }
  };

  const eligible = products.filter((p) => !existingCampaignProductIds.has(p.id));

  return (
    <div className="space-y-6" data-testid="new-round-page">
      <div>
        <Link to="/app/rounds" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft size={14} /> Back to rounds
        </Link>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-3">Create launch round</h1>
        <p className="text-sm text-gray-600 mt-2">
          Pick 2+ products to compete. Customers vote, and only the top winners advance to pre-order when the round ends.
        </p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="lp-card p-6">
            <h2 className="text-base font-semibold tracking-tight mb-1">Round details</h2>
            <p className="text-xs text-gray-500 mb-5">Name your round and set a deadline.</p>
            <div className="space-y-5">
              <div>
                <label className="lp-label" htmlFor="round-name">Round name</label>
                <input
                  id="round-name"
                  className="lp-input"
                  data-testid="round-name-input"
                  placeholder="Spring Capsule 2026"
                  required
                  maxLength={80}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label className="lp-label" htmlFor="round-desc">Description (optional)</label>
                <textarea
                  id="round-desc"
                  className="lp-input"
                  data-testid="round-desc-input"
                  rows={2}
                  placeholder="Help us decide which 2 styles join our spring drop."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="lp-label" htmlFor="winners">Winners</label>
                  <input
                    id="winners"
                    type="number"
                    min="1"
                    className="lp-input"
                    data-testid="round-winners-input"
                    value={winnersCount}
                    onChange={(e) => setWinnersCount(e.target.value)}
                    required
                  />
                  <div className="lp-help">Top-voted products that advance to pre-order.</div>
                </div>
                <div>
                  <label className="lp-label" htmlFor="end">Ends on</label>
                  <input
                    id="end"
                    type="datetime-local"
                    className="lp-input"
                    data-testid="round-end-input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                  <div className="lp-help">Winners are picked automatically at this time.</div>
                </div>
              </div>
            </div>
          </section>

          <section className="lp-card p-6">
            <h2 className="text-base font-semibold tracking-tight mb-1">Pick competing products</h2>
            <p className="text-xs text-gray-500 mb-5">
              Select 2 or more products. Products already in a regular campaign are not eligible.
            </p>

            {eligible.length === 0 ? (
              <div className="p-6 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3" data-testid="no-eligible-products">
                <AlertTriangle size={18} className="text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-900">
                  Every product already has a campaign. Delete an existing campaign first, or call <code className="bg-amber-100 px-1 rounded">demo/reset</code> from the dashboard.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="round-product-grid">
                {eligible.map((p) => {
                  const checked = selectedIds.includes(p.id);
                  return (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => toggle(p.id)}
                      data-testid={`round-product-${p.id}`}
                      className={`text-left p-3 rounded-xl border transition-all flex items-center gap-3 ${
                        checked
                          ? "bg-gray-900 text-white border-gray-900"
                          : "bg-white border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      <img src={p.imageUrl} alt="" className="w-12 h-12 object-cover rounded-lg bg-gray-100" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{p.title}</div>
                        <div className={`text-xs ${checked ? "text-gray-300" : "text-gray-500"}`}>
                          ${p.priceRange.min.toFixed(2)} · {p.vendor}
                        </div>
                      </div>
                      {checked && <Check size={16} />}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="lp-card p-6">
            <h2 className="text-base font-semibold tracking-tight mb-1">Pre-order settings for winners</h2>
            <p className="text-xs text-gray-500 mb-5">Applied to whichever products become winners.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="lp-label" htmlFor="po-price">Pre-order price ($, optional)</label>
                <input
                  id="po-price"
                  type="number"
                  step="0.01"
                  className="lp-input"
                  data-testid="round-price-input"
                  value={preOrderPrice}
                  onChange={(e) => setPreOrderPrice(e.target.value)}
                  placeholder="Defaults to product price"
                />
              </div>
              <div>
                <label className="lp-label" htmlFor="ship">Estimated ship date</label>
                <input
                  id="ship"
                  type="date"
                  className="lp-input"
                  data-testid="round-ship-input"
                  value={estimatedShipDate}
                  onChange={(e) => setEstimatedShipDate(e.target.value)}
                />
              </div>
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <div className="lp-card p-6 sticky top-6">
            <div className="lp-section-label">Summary</div>
            <h3 className="text-lg font-medium tracking-tight mt-2">{name || "Untitled round"}</h3>
            <div className="mt-5 space-y-3 text-sm">
              <Row label="Competing" value={`${selectedIds.length} product${selectedIds.length === 1 ? "" : "s"}`} />
              <Row label="Winners" value={`Top ${winnersCount}`} />
              <Row label="Ends" value={endDate ? new Date(endDate).toLocaleString() : "—"} />
              <Row label="Pre-order price" value={preOrderPrice ? `$${Number(preOrderPrice).toFixed(2)}` : "Product price"} />
              <Row label="Ship date" value={estimatedShipDate || "—"} />
            </div>
            <button
              type="submit"
              disabled={submitting || selectedIds.length < 2}
              className="lp-btn-primary w-full justify-center mt-6"
              data-testid="submit-round-btn"
            >
              {submitting ? "Creating…" : (<><Trophy size={16} /> Start round</>)}
            </button>
            <div className="text-[11px] text-gray-500 mt-3 text-center">
              You can edit name, description, winners, and end date until the round finalizes.
            </div>
          </div>

          <div className="lp-card p-6 bg-gradient-to-br from-gray-50 to-white">
            <div className="flex items-center gap-2 mb-2">
              <Check size={14} className="text-emerald-600" />
              <div className="text-xs font-semibold">How it works</div>
            </div>
            <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside leading-relaxed">
              <li>Customers see the round on your storefront with a live leaderboard.</li>
              <li>One launch vote per email per product.</li>
              <li>When the deadline hits, top-voted winners flip to pre-order.</li>
              <li>Losing products are closed, voters are emailed about the winners.</li>
            </ol>
          </div>
        </aside>
      </form>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium text-gray-900 text-right ml-3 truncate max-w-[60%]">{value}</span>
  </div>
);
